/**
 * PetFed Wallet — storefront controller (DIRECT API VERSION)
 * -------------------------------------------------------------
 * ⚠ This version calls https://staging-api.petfed.org directly from
 * the browser, instead of going through the same-origin Shopify App
 * Proxy (/apps/wallet/*). It will NOT work until the PetFed backend
 * team adds CORS (see cors-middleware-for-backend-team.js) — until
 * then every call below fails at the browser's preflight step.
 *
 * ⚠ SECURITY: the PetFed bearer token is now stored in the browser
 * (localStorage) so it survives a page reload, since there's no
 * server-side proxy session to hold it anymore. Any XSS on this
 * storefront, or any other script/app installed on the store, can
 * read it and spend the customer's wallet. The App Proxy version
 * (assets/wallet.js) does not have this exposure — keep that version
 * unless you specifically need this one.
 *
 * All money is integer paise end to end. Division by 100 happens only
 * in the format helpers below, right before it hits the DOM.
 */
(function () {
  'use strict';

  const RAZORPAY_SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
  const POLL_INTERVAL_MS = 2000;
  const POLL_CEILING_MS = 30000;
  const RESEND_COOLDOWN_S = 30;
  const TOKEN_STORAGE_KEY = 'petfed_wallet_token';
  const TOKEN_EXPIRY_STORAGE_KEY = 'petfed_wallet_token_expires_at';

  function formatRupees(paise) {
    const rupees = (paise || 0) / 100;
    return '₹' + rupees.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  function formatDate(iso) {
    try {
      return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) {
      return iso;
    }
  }

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function show(el) {
    if (el) el.hidden = false;
  }

  function hide(el) {
    if (el) el.hidden = true;
  }

  function loadRazorpayScript() {
    if (window.Razorpay) return Promise.resolve();
    if (window.__petfedRazorpayLoading) return window.__petfedRazorpayLoading;
    window.__petfedRazorpayLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = RAZORPAY_SCRIPT_SRC;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Could not load Razorpay checkout script'));
      document.head.appendChild(script);
    });
    return window.__petfedRazorpayLoading;
  }

  // ---------- token storage (replaces the App Proxy's server-side session) ----------
  function getStoredToken() {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const expiresAt = localStorage.getItem(TOKEN_EXPIRY_STORAGE_KEY);
    if (!token) return null;
    if (expiresAt && new Date(expiresAt).getTime() <= Date.now()) {
      clearStoredToken();
      return null;
    }
    return token;
  }

  function setStoredToken(token, expiresAt) {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    if (expiresAt) localStorage.setItem(TOKEN_EXPIRY_STORAGE_KEY, expiresAt);
  }

  function clearStoredToken() {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_STORAGE_KEY);
  }

  class WalletApp {
    constructor(root) {
      this.root = root;
      // Full origin now, not a same-origin path — set this via
      // data-api-base="https://staging-api.petfed.org" on the section,
      // and swap to the production host at cutover.
      this.base = root.dataset.apiBase || 'https://api.petfed.org';
      this.wallet = null;
      this.pendingChallengeId = null;
      this.pendingEmail = null;
      this.ledgerCursor = null;
      this.resendTimer = null;

      this.els = {
        loading: qs(root, '[data-wallet-loading]'),
        login: qs(root, '[data-wallet-login]'),
        blocked: qs(root, '[data-wallet-blocked]'),
        blockedMessage: qs(root, '[data-wallet-blocked-message]'),
        main: qs(root, '[data-wallet-main]'),
        fatal: qs(root, '[data-wallet-fatal-error]'),
        fatalMessage: qs(root, '[data-wallet-fatal-error-message]'),
        fatalRequestId: qs(root, '[data-wallet-request-id]'),
        retry: qs(root, '[data-wallet-retry]'),

        otpRequestForm: qs(root, '[data-wallet-otp-request-form]'),
        otpRequestError: qs(root, '[data-wallet-otp-request-error]'),
        emailInput: qs(root, '[data-wallet-email-input]'),
        otpVerifyForm: qs(root, '[data-wallet-otp-verify-form]'),
        otpVerifyError: qs(root, '[data-wallet-otp-verify-error]'),
        codeInput: qs(root, '[data-wallet-code-input]'),
        otpSentTo: qs(root, '[data-wallet-otp-sent-to]'),
        resendBtn: qs(root, '[data-wallet-resend]'),

        brandName: qs(root, '[data-wallet-brand-name]'),
        cashAvailable: qs(root, '[data-wallet-cash-available]'),
        cashHeld: qs(root, '[data-wallet-cash-held]'),
        pointsBalance: qs(root, '[data-wallet-points-balance]'),
        pointsValue: qs(root, '[data-wallet-points-value]'),
        pointsExpiring: qs(root, '[data-wallet-points-expiring]'),
        rules: qs(root, '[data-wallet-rules]'),
        logout: qs(root, '[data-wallet-logout]'),

        ledgerList: qs(root, '[data-wallet-ledger-list]'),
        ledgerEmpty: qs(root, '[data-wallet-ledger-empty]'),
        ledgerMore: qs(root, '[data-wallet-ledger-more]'),

        openTopup: qs(root, '[data-wallet-open-topup]'),
      };

      this.modal = document.querySelector('[data-wallet-topup-modal]');
      this.modalEls = this.modal
        ? {
            formState: qs(this.modal, '[data-wallet-topup-form-state]'),
            pendingState: qs(this.modal, '[data-wallet-topup-pending-state]'),
            delayedState: qs(this.modal, '[data-wallet-topup-delayed-state]'),
            headroom: qs(this.modal, '[data-wallet-topup-headroom]'),
            amountInput: qs(this.modal, '[data-wallet-topup-amount-input]'),
            payBtn: qs(this.modal, '[data-wallet-topup-pay]'),
            error: qs(this.modal, '[data-wallet-topup-error]'),
            closeBtns: this.modal.querySelectorAll('[data-wallet-topup-close]'),
          }
        : null;

      this.bindEvents();
      this.boot();
    }

    // ---------- low-level fetch helper ----------
    // Talks directly to the PetFed API. Requires CORS to be enabled
    // server-side (see cors-middleware-for-backend-team.js) or every
    // call below fails at the preflight with a generic "Failed to
    // fetch" / opaque network error — the browser won't even show you
    // the CORS reason in response.status.
    async call(path, { method = 'GET', body, auth = true } = {}) {
      const headers = { 'Content-Type': 'application/json' };
      if (auth) {
        const token = getStoredToken();
        if (token) headers.Authorization = 'Bearer ' + token;
      }

      let response;
      try {
        response = await fetch(this.base + path, {
          method,
          credentials: 'omit', // bearer token, not cookies — omit avoids an extra CORS credential check
          headers,
          body: body ? JSON.stringify(body) : undefined,
        });
      } catch (networkErr) {
        // A CORS rejection also lands here, indistinguishable from a
        // real network error — that's a limitation of fetch(), not a
        // bug in this code.
        return { ok: false, status: 0, data: { error: { code: 'NETWORK_ERROR', message: 'Network error' } } };
      }
      let data = {};
      try {
        data = await response.json();
      } catch (e) {
        /* empty body */
      }
      if (response.status === 429) {
        data.__retryAfter = response.headers.get('Retry-After');
      }
      if (response.status === 401) {
        clearStoredToken();
      }
      return { ok: response.ok, status: response.status, data };
    }

    // ---------- boot / state machine ----------
    async boot() {
      show(this.els.loading);
      hide(this.els.login);
      hide(this.els.main);
      hide(this.els.blocked);
      hide(this.els.fatal);

      if (!getStoredToken()) {
        hide(this.els.loading);
        show(this.els.login);
        return;
      }

      const { ok, status, data } = await this.call('/v1/wallet');
      hide(this.els.loading);

      if (ok) {
        this.wallet = data;
        this.renderWallet();
        show(this.els.main);
        this.loadLedger(true);
        this.loadRules();
        return;
      }

      const code = data && data.error && data.error.code;

      if (status === 401) {
        show(this.els.login);
        return;
      }

      if (status === 403) {
        this.els.blockedMessage.textContent =
          'This account is currently blocked. Please contact support.';
        show(this.els.blocked);
        return;
      }

      if (status === 423) {
        this.els.blockedMessage.textContent =
          'Account deletion is in progress. Restore your account to use the wallet again.';
        show(this.els.blocked);
        return;
      }

      this.showFatalError(data);
    }

    showFatalError(data) {
      const err = (data && data.error) || {};
      this.els.fatalMessage.textContent =
        err.message || 'Something went wrong loading your wallet. Please try again.';
      if (err.request_id) {
        this.els.fatalRequestId.textContent = 'Reference: ' + err.request_id;
        show(this.els.fatalRequestId);
      } else {
        hide(this.els.fatalRequestId);
      }
      show(this.els.fatal);
    }

    // ---------- rendering ----------
    renderWallet() {
      const w = this.wallet;
      if (!w) return;

      this.els.brandName.textContent = w.wallet_name || this.root.dataset.fallbackName || 'Pet Fed Wallet';

      this.els.cashAvailable.textContent = formatRupees(w.cash.available_paise);
      if (w.cash.held_paise > 0) {
        this.els.cashHeld.textContent = formatRupees(w.cash.held_paise) + ' on hold';
        show(this.els.cashHeld);
      } else {
        hide(this.els.cashHeld);
      }

      this.els.pointsBalance.textContent = w.points.balance + ' pts';
      this.els.pointsValue.textContent = 'Worth ' + formatRupees(w.points.value_paise);

      if (w.points.expiring_soon && w.points.expiring_soon.points > 0) {
        this.els.pointsExpiring.textContent =
          w.points.expiring_soon.points +
          ' pts expire ' +
          formatDate(w.points.expiring_soon.expires_at);
        show(this.els.pointsExpiring);
      } else {
        hide(this.els.pointsExpiring);
      }
    }

    async loadRules() {
      // No auth needed upstream either way.
      const { ok, data } = await this.call('/v1/rewards/rules', { auth: false });
      if (ok && Array.isArray(data.copy)) {
        this.els.rules.textContent = data.copy.join(' ');
        show(this.els.rules);
      }
    }

    async loadLedger(reset) {
      if (reset) {
        this.ledgerCursor = null;
        if (this.els.ledgerList) this.els.ledgerList.innerHTML = '';
      }
      const params = new URLSearchParams({ limit: '20' });
      if (this.ledgerCursor) params.set('cursor', this.ledgerCursor);

      const { ok, data } = await this.call('/v1/wallet/ledger?' + params.toString());
      if (!ok) return;

      const entries = data.entries || [];
      if (reset && entries.length === 0) {
        show(this.els.ledgerEmpty);
      } else {
        hide(this.els.ledgerEmpty);
      }

      entries.forEach((entry) => this.renderLedgerEntry(entry));

      this.ledgerCursor = data.next_cursor;
      if (this.els.ledgerMore) {
        this.els.ledgerMore.hidden = !this.ledgerCursor;
      }
    }

    renderLedgerEntry(entry) {
      if (!this.els.ledgerList) return;
      const row = document.createElement('li');
      row.className = 'wallet-ledger-row';

      const isCash = entry.pocket === 'cash';
      const delta = isCash ? entry.delta_paise : entry.delta;
      const label = isCash ? entry.type : entry.reason;
      const amountText = isCash
        ? (delta >= 0 ? '+' : '') + formatRupees(delta)
        : (delta >= 0 ? '+' : '') + delta + ' pts';

      row.innerHTML =
        '<span class="wallet-ledger-label">' + label + '</span>' +
        '<span class="wallet-ledger-amount ' + (delta >= 0 ? 'is-credit' : 'is-debit') + '">' +
        amountText +
        '</span>' +
        '<span class="wallet-ledger-date">' + formatDate(entry.created_at) + '</span>';

      this.els.ledgerList.appendChild(row);
    }

    // ---------- events ----------
    bindEvents() {
      if (this.els.otpRequestForm) {
        this.els.otpRequestForm.addEventListener('submit', (e) => this.onOtpRequestSubmit(e));
      }
      if (this.els.otpVerifyForm) {
        this.els.otpVerifyForm.addEventListener('submit', (e) => this.onOtpVerifySubmit(e));
      }
      if (this.els.resendBtn) {
        this.els.resendBtn.addEventListener('click', () => this.onOtpRequestSubmit(null, true));
      }
      if (this.els.logout) {
        this.els.logout.addEventListener('click', () => this.onLogout());
      }
      if (this.els.retry) {
        this.els.retry.addEventListener('click', () => this.boot());
      }
      if (this.els.openTopup) {
        this.els.openTopup.addEventListener('click', () => this.openTopupModal());
      }
      if (this.els.ledgerMore) {
        this.els.ledgerMore.addEventListener('click', () => this.loadLedger(false));
      }
      if (this.modalEls) {
        this.modalEls.payBtn.addEventListener('click', () => this.onPayNow());
        this.modalEls.closeBtns.forEach((btn) =>
          btn.addEventListener('click', () => this.closeTopupModal())
        );
      }
    }

    async onOtpRequestSubmit(e, isResend) {
      if (e) e.preventDefault();
      const email = isResend ? this.pendingEmail : this.els.emailInput.value.trim();
      if (!email) return;
      hide(this.els.otpRequestError);

      const { ok, status, data } = await this.call('/v1/auth/otp/request', {
        method: 'POST',
        body: { email },
        auth: false,
      });

      if (!ok) {
        this.handleAuthError(status, data, this.els.otpRequestError);
        return;
      }

      this.pendingChallengeId = data.challenge_id;
      this.pendingEmail = email;
      this.els.otpSentTo.textContent = 'Code sent to ' + email;
      hide(this.els.otpRequestForm);
      show(this.els.otpVerifyForm);
      this.startResendCooldown();
    }

    startResendCooldown() {
      let remaining = RESEND_COOLDOWN_S;
      this.els.resendBtn.disabled = true;
      const update = () => {
        this.els.resendBtn.textContent =
          remaining > 0 ? `Resend code (${remaining}s)` : 'Resend code';
        if (remaining <= 0) {
          this.els.resendBtn.disabled = false;
          clearInterval(this.resendTimer);
        }
        remaining -= 1;
      };
      clearInterval(this.resendTimer);
      update();
      this.resendTimer = setInterval(update, 1000);
    }

    async onOtpVerifySubmit(e) {
      e.preventDefault();
      const code = this.els.codeInput.value.trim();
      if (!code || !this.pendingChallengeId) return;
      hide(this.els.otpVerifyError);

      const { ok, status, data } = await this.call('/v1/auth/otp/verify', {
        method: 'POST',
        body: { challenge_id: this.pendingChallengeId, code },
        auth: false,
      });

      if (!ok) {
        this.handleAuthError(status, data, this.els.otpVerifyError);
        return;
      }

      // Unlike the App Proxy version, the token IS in this response —
      // store it client-side since there's no server-side session here.
      if (data.token) {
        setStoredToken(data.token, data.expires_at);
      }
      this.boot();
    }

    handleAuthError(status, data, errorEl) {
      const err = (data && data.error) || {};
      let message = err.message || 'Something went wrong. Please try again.';

      if (err.code === 'AUTH_OTP_INVALID' && err.details && typeof err.details.attempts_remaining === 'number') {
        message = `That code is incorrect. ${err.details.attempts_remaining} attempt(s) left.`;
      } else if (err.code === 'AUTH_OTP_EXPIRED') {
        message = 'That code expired. Send a new one.';
      } else if (err.code === 'AUTH_OTP_ATTEMPTS_EXCEEDED') {
        message = 'Too many incorrect attempts. Please request a new code.';
        hide(this.els.otpVerifyForm);
        show(this.els.otpRequestForm);
      } else if (status === 429) {
        const retryAfter = data.__retryAfter || (err.details && err.details.retry_after_seconds);
        message = retryAfter
          ? `Too many attempts. Try again in ${retryAfter} seconds.`
          : 'Too many attempts. Please wait a moment and try again.';
      } else if (status === 0) {
        message = 'Could not reach the wallet service. This can mean CORS is not yet enabled on the API — check with the backend team.';
      }

      errorEl.textContent = message;
      show(errorEl);
    }

    async onLogout() {
      const token = getStoredToken();
      if (token) {
        await this.call('/v1/auth/session/logout', { method: 'POST' }).catch(() => {});
      }
      clearStoredToken();
      this.wallet = null;
      hide(this.els.main);
      this.els.otpRequestForm && show(this.els.otpRequestForm);
      this.els.otpVerifyForm && hide(this.els.otpVerifyForm);
      show(this.els.login);
    }

    // ---------- top-up ----------
    openTopupModal() {
      if (!this.modal || !this.wallet) return;
      const headroom = this.wallet.cash.topup_headroom_paise;
      this.modalEls.headroom.textContent =
        headroom > 0
          ? `You can add up to ${formatRupees(headroom)} before hitting the wallet cap.`
          : 'You are at the wallet cap — no further top-up is possible right now.';
      this.modalEls.amountInput.disabled = headroom <= 0;
      this.modalEls.payBtn.disabled = headroom <= 0;
      this.modalEls.amountInput.value = '';
      hide(this.modalEls.error);
      show(this.modalEls.formState);
      hide(this.modalEls.pendingState);
      hide(this.modalEls.delayedState);
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
    }

    closeTopupModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
    }

    async onPayNow() {
      const rupees = parseFloat(this.modalEls.amountInput.value);
      if (!rupees || rupees <= 0) {
        this.modalEls.error.textContent = 'Enter an amount to add.';
        show(this.modalEls.error);
        return;
      }
      const amountPaise = Math.round(rupees * 100);
      hide(this.modalEls.error);
      this.modalEls.payBtn.disabled = true;

      const { ok, status, data } = await this.call('/v1/wallet/topup', {
        method: 'POST',
        body: { amount_paise: amountPaise },
      });

      this.modalEls.payBtn.disabled = false;

      if (!ok) {
        this.modalEls.error.textContent = this.describeTopupError(status, data);
        show(this.modalEls.error);
        return;
      }

      try {
        await loadRazorpayScript();
      } catch (e) {
        this.modalEls.error.textContent = 'Could not load the payment window. Please try again.';
        show(this.modalEls.error);
        return;
      }

      const balanceBefore = this.wallet.cash.balance_paise;

      const rzp = new window.Razorpay({
        key: data.key_id, 
        order_id: data.razorpay_order_id,
        amount: data.amount_paise,
        currency: 'INR',
        name: (this.wallet && this.wallet.wallet_name) || 'Pet Fed Wallet',
        description: 'Wallet top-up',
        handler: () => {
          // Payment succeeded client-side. The wallet is credited by the
          // backend webhook, not by this callback — poll until it lands.
          this.showTopupPending();
          this.pollForCredit(balanceBefore);
        },
        modal: {
          ondismiss: () => {
            /* customer cancelled — leave the form state visible */
          },
        },
      });

      rzp.open();
    }

    describeTopupError(status, data) {
      const err = (data && data.error) || {};
      if (err.code === 'WALLET_CAP_EXCEEDED') {
        const headroom = err.details && err.details.headroomPaise;
        return headroom
          ? `That would exceed your wallet cap. You can add up to ${formatRupees(headroom)}.`
          : 'That would exceed your wallet cap.';
      }
      if (err.code === 'BAD_REQUEST') {
        const min = err.details && err.details.minPaise;
        const max = err.details && err.details.maxPaise;
        if (min || max) {
          return `Enter an amount between ${formatRupees(min || 0)} and ${formatRupees(max || 0)}.`;
        }
      }
      if (status === 401) {
        return 'Your session expired — please log in again.';
      }
      return err.message || 'Could not start the top-up. Please try again.';
    }

    showTopupPending() {
      hide(this.modalEls.formState);
      hide(this.modalEls.delayedState);
      show(this.modalEls.pendingState);
    }

    showTopupDelayed() {
      hide(this.modalEls.pendingState);
      show(this.modalEls.delayedState);
    }

    async pollForCredit(balanceBefore) {
      const startedAt = Date.now();

      const tick = async () => {
        const { ok, data } = await this.call('/v1/wallet');
        if (ok) {
          this.wallet = data;
          if (data.cash.balance_paise !== balanceBefore) {
            this.renderWallet();
            this.loadLedger(true);
            this.closeTopupModal();
            return;
          }
        }
        if (Date.now() - startedAt >= POLL_CEILING_MS) {
          this.showTopupDelayed();
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      };

      setTimeout(tick, POLL_INTERVAL_MS);
    }
  }

  function init() {
    document.querySelectorAll('[data-wallet-root]').forEach((root) => {
      if (!root.__walletApp) {
        root.__walletApp = new WalletApp(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
